import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { X, Plus, ExternalLink } from 'lucide-react';

export default function AddIdeaForm({ onClose }) {
  const { addIdea, family } = useFamilyContext();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'project',
    targetKids: [],
    links: ['']
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      alert('Please fill in the title and description');
      return;
    }

    if (formData.targetKids.length === 0) {
      alert('Please select at least one kid');
      return;
    }

    const ideaData = {
      ...formData,
      links: formData.links.filter(link => link.trim() !== ''),
      kidResponses: {}
    };

    addIdea(ideaData);
    onClose();
  };

  const handleKidToggle = (kidId) => {
    setFormData(prev => ({
      ...prev,
      targetKids: prev.targetKids.includes(kidId)
        ? prev.targetKids.filter(id => id !== kidId)
        : [...prev.targetKids, kidId]
    }));
  };

  const addLinkField = () => {
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, '']
    }));
  };

  const updateLink = (index, value) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.map((link, i) => i === index ? value : link)
    }));
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      category: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-stone-900">Add New Idea</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Learn to code with Scratch"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select category</option>
              <option value="book">Book</option>
              <option value="project">Project</option>
              <option value="experience">Experience</option>
              <option value="learning">Learning</option>
              <option value="recipe">Recipe</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              For Which Kids?
            </label>
            <div className="space-y-2">
              {family?.kids.map(kid => (
                <label key={kid.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.targetKids.includes(kid.id)}
                    onChange={() => handleKidToggle(kid.id)}
                    className="rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-stone-700">{kid.name} (age {kid.age})</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={8}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[120px]"
              placeholder="Describe what this idea is about, why it's interesting, and how to get started. Include details about what they'll learn, what materials they need, expected time commitment, skill level required, etc..."
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-stone-700">
                Links (Optional)
              </label>
              <button
                type="button"
                onClick={addLinkField}
                className="inline-flex items-center px-2 py-1 text-xs bg-indigo-100 text-primary-700 rounded hover:bg-indigo-200 transition-colors"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Link
              </button>
            </div>
            <div className="space-y-2">
              {formData.links.map((link, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-1 relative">
                    <ExternalLink className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => updateLink(index, e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com"
                    />
                  </div>
                  {formData.links.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLink(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-neutral-50 font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Idea
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-stone-200 text-stone-700 font-medium rounded-lg hover:bg-stone-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}